<template>
    <div>
        <div class="p-grid">
            <div class="p-col-12">
                <div class="card">
                    <div class="flex justify-between items-center mb-4">
                        <div>
                            <Link :href="route('admin.index')" class="text-blue-600 hover:underline">
                                ← Kembali ke Admin Management
                            </Link>
                            <h2 class="text-2xl font-bold mt-2">Role Management</h2>
                        </div>
                        <Link :href="route('admin.roles.create')">
                            <Button label="Tambah Role" icon="pi pi-plus" class="p-button-primary" />
                        </Link>
                    </div>
                </div>
            </div>
            
            <div class="p-col-12">
                <div class="card">
                    <!-- Search -->
                    <div class="mb-4">
                        <span class="p-input-icon-left w-full md:w-1/3">
                            <i class="pi pi-search" />
                            <InputText v-model="search" placeholder="Cari role..." class="w-full" @input="onSearch" />
                        </span>
                    </div>
                    
                    <DataTable 
                        :value="roles" 
                        :lazy="true" 
                        :paginator="true" 
                        :rows="10"
                        :loading="loading" 
                        :total-records="totalRecords"
                        :rows-per-page-options="[5, 10, 25, 50]"
                        paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        current-page-report-template="Menampilkan {first} sampai {last} dari {totalRecords} role"
                        @page="onPage($event)" 
                        @sort="onSort($event)"
                        stripedRows
                        responsiveLayout="scroll"
                    >
                        <Column field="id" header="ID" :sortable="true" style="width: 80px"></Column>
                        <Column field="name" header="Nama" :sortable="true"></Column>
                        <Column field="kode" header="Kode" :sortable="true"></Column>
                        <Column field="title" header="Title" :sortable="true"></Column>
                        <Column field="icon" header="Icon" style="width: 100px">
                            <template #body="slotProps">
                                <i :class="'pi ' + slotProps.data.icon" class="text-lg"></i>
                            </template>
                        </Column>
                        <Column field="has_scope" header="Has Scope" style="width: 100px">
                            <template #body="slotProps">
                                <Tag :value="slotProps.data.has_scope ? 'Ya' : 'Tidak'" 
                                     :severity="slotProps.data.has_scope ? 'success' : 'secondary'" />
                            </template>
                        </Column>
                        <Column field="assignable" header="Assignable" style="width: 100px">
                            <template #body="slotProps">
                                <Tag :value="slotProps.data.assignable ? 'Ya' : 'Tidak'" 
                                     :severity="slotProps.data.assignable ? 'success' : 'secondary'" />
                            </template>
                        </Column>
                        <Column header="Aksi" style="width: 100px">
                            <template #body="slotProps">
                                <div class="flex gap-2">
                                    <Button icon="pi pi-pencil" class="p-button-info p-button-sm" 
                                            @click="editRole(slotProps.data)" v-tooltip="'Edit'" />
                                </div>
                            </template>
                        </Column>
                        <template #empty>
                            <div class="text-center py-4">Tidak ada role ditemukan</div>
                        </template>
                    </DataTable>
                </div>
            </div>
        </div>
        
        <!-- Delete Confirmation Dialog -->
        <Dialog v-model:visible="deleteDialog" :style="{width: '450px'}" header="Konfirmasi Hapus" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: var(--red-500)" />
                <span v-if="selectedRole">Apakah Anda yakin ingin menghapus role <b>{{ selectedRole.name }}</b>?</span>
            </div>
            <template #footer>
                <Button label="Batal" icon="pi pi-times" class="p-button-text" @click="deleteDialog = false" />
                <Button label="Hapus" icon="pi pi-trash" class="p-button-danger" @click="deleteRole" :loading="deleting" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Dialog from "primevue/dialog";
import Tag from "primevue/tag";
import { Link } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';

export default {
    name: "RolesIndex",
    layout: AppLayout,
    components: {
        DataTable,
        Column,
        Button,
        InputText,
        Dialog,
        Tag,
        Link,
    },
    data() {
        return {
            roles: [],
            loading: true,
            totalRecords: 0,
            lazyParams: {
                first: 0,
                rows: 10,
                sortField: 'id',
                sortOrder: 1,
            },
            search: '',
            searchTimeout: null,
            deleteDialog: false,
            selectedRole: null,
            deleting: false,
        };
    },
    mounted() {
        this.loadData();
    },
    methods: {
        loadData() {
            this.loading = true;
            
            const params = {
                page: Math.floor(this.lazyParams.first / this.lazyParams.rows) + 1,
                rows: this.lazyParams.rows,
                sortField: this.lazyParams.sortField,
                sortOrder: this.lazyParams.sortOrder,
                search: this.search,
            };
            
            fetch(this.route('admin.roles.data') + '?' + new URLSearchParams(params))
                .then(response => response.json())
                .then(data => {
                    this.roles = data.data;
                    this.totalRecords = data.total;
                    this.loading = false;
                });
        },
        onPage(event) {
            this.lazyParams = event;
            this.loadData();
        },
        onSort(event) {
            this.lazyParams = event;
            this.loadData();
        },
        onSearch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.lazyParams.first = 0;
                this.loadData();
            }, 300);
        },
        editRole(role) {
            Inertia.visit(this.route('admin.roles.edit', role.id));
        },
        confirmDelete(role) {
            this.selectedRole = role;
            this.deleteDialog = true;
        },
        deleteRole() {
            this.deleting = true;
            Inertia.delete(this.route('admin.roles.delete', this.selectedRole.id), {
                onSuccess: () => {
                    this.deleteDialog = false;
                    this.deleting = false;
                    this.loadData();
                },
                onError: () => {
                    this.deleting = false;
                }
            });
        },
    },
}
</script>

<style scoped>
.confirmation-content {
    display: flex;
    align-items: center;
}
</style>
